import React, { useState } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { useDeliverableBulkOps } from '../hooks/useDeliverableBulkOps';
import { DeliverableStatus } from '../types/deliverable';

interface BulkDeliverableActionsProps {
  selectedIds: Set<string>;
  onDeselect: () => void;
  onSuccess: () => void;
}

type ActiveAction = 'status' | 'reassign' | 'deadline' | null;

const STATUS_OPTIONS: DeliverableStatus[] = [
  'IN_PROGRESS',
  'DELIVERED',
  'VERIFIED',
  'REJECTED',
  'DELAYED',
];

export const BulkDeliverableActions: React.FC<BulkDeliverableActionsProps> = ({
  selectedIds,
  onDeselect,
  onSuccess,
}) => {
  const { executeBulkAction, isLoading } = useDeliverableBulkOps();

  const [activeAction, setActiveAction] = useState<ActiveAction>(null);
  const [statusValue, setStatusValue] = useState<DeliverableStatus>('IN_PROGRESS');
  const [assigneeValue, setAssigneeValue] = useState('');
  const [deadlineValue, setDeadlineValue] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (selectedIds.size === 0) return null;

  const deliverableIds = Array.from(selectedIds);

  const showToast = (message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  };

  const handleConfirm = async () => {
    if (!activeAction) return;
    setError(null);

    try {
      let result;

      if (activeAction === 'status') {
        result = await executeBulkAction({
          action: 'UPDATE_STATUS',
          deliverableIds,
          payload: { status: statusValue },
        });
      } else if (activeAction === 'reassign') {
        if (!assigneeValue.trim()) {
          setError('Please enter an assignee ID');
          return;
        }
        result = await executeBulkAction({
          action: 'REASSIGN',
          deliverableIds,
          payload: { assignedToId: assigneeValue.trim() },
        });
      } else if (activeAction === 'deadline') {
        if (!deadlineValue) {
          setError('Please select a date');
          return;
        }
        result = await executeBulkAction({
          action: 'EXTEND_DEADLINE',
          deliverableIds,
          payload: { committedDate: deadlineValue },
        });
      }

      if (result) {
        showToast(`${result.succeeded.length} succeeded, ${result.failed.length} failed`);
        setActiveAction(null);
        onSuccess();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    }
  };

  const handleCancel = () => {
    setActiveAction(null);
    setError(null);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-lg px-6 py-3">
      <div className="max-w-7xl mx-auto flex items-center gap-4 flex-wrap">
        {/* Selected count */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">
            {selectedIds.size} selected
          </span>
          <button
            onClick={onDeselect}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
            title="Deselect all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="w-px h-6 bg-gray-200" />

        {/* Action buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Update Status */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setActiveAction(activeAction === 'status' ? null : 'status')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors"
            >
              Update Status
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {activeAction === 'status' && (
              <div className="flex items-center gap-2">
                <select
                  value={statusValue}
                  onChange={(e) => setStatusValue(e.target.value as DeliverableStatus)}
                  className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-indigo-500"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s.replace('_', ' ')}</option>
                  ))}
                </select>
                <button
                  onClick={handleConfirm}
                  disabled={isLoading}
                  className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  {isLoading ? '...' : 'Apply'}
                </button>
                <button onClick={handleCancel} className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors">
                  Cancel
                </button>
              </div>
            )}
          </div>

          {/* Reassign */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setActiveAction(activeAction === 'reassign' ? null : 'reassign')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors"
            >
              Reassign
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {activeAction === 'reassign' && (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={assigneeValue}
                  onChange={(e) => setAssigneeValue(e.target.value)}
                  placeholder="User ID..."
                  className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 w-40 focus:ring-2 focus:ring-purple-500"
                />
                <button
                  onClick={handleConfirm}
                  disabled={isLoading}
                  className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                >
                  {isLoading ? '...' : 'Apply'}
                </button>
                <button onClick={handleCancel} className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors">
                  Cancel
                </button>
              </div>
            )}
          </div>

          {/* Extend Deadline */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setActiveAction(activeAction === 'deadline' ? null : 'deadline')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-50 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors"
            >
              Extend Deadline
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {activeAction === 'deadline' && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={deadlineValue}
                  onChange={(e) => setDeadlineValue(e.target.value)}
                  className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-slate-500"
                />
                <button
                  onClick={handleConfirm}
                  disabled={isLoading}
                  className="px-3 py-1.5 bg-slate-600 text-white text-sm rounded-lg hover:bg-slate-700 disabled:opacity-50 transition-colors"
                >
                  {isLoading ? '...' : 'Apply'}
                </button>
                <button onClick={handleCancel} className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors">
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <span className="text-sm text-red-500">{error}</span>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-16 right-6 bg-gray-800 text-white text-sm px-4 py-2 rounded-lg shadow-lg z-50">
          {toast}
        </div>
      )}
    </div>
  );
};

export default BulkDeliverableActions;
